import { useGuardedDefaultValues } from "@msuite/ui/hooks";
import { resolver } from "@msuite/ui/libs/form";
import { useTranslation } from "@msuite/ui/localization";
import { Modal, type ModalProps } from "@msuite/ui/modal";
import { responseHandler } from "@msuite/ui/utils";
import { guardArray } from "@msuite/utils/array";
import {
	type ReturnOrderVehiclesFormSchema,
	returnOrderVehiclesFormSchema,
} from "@msuite/utils/schemas";
import { Fragment } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { ModalLoadFailed } from "@/components/modals";
import { ReturnOrderFormVehicles } from "@/forms/return-order-form";
import { client } from "@/hono";

/** Props Interface */
interface EditReturnOrderVehiclesModalProps extends ModalProps {
	returnOrderId: string;
	onComplete?: () => Promise<unknown> | undefined;
}

/**
 * Change which lorries are fetching a site's material.
 *
 * The fleet is the only part of a filed return order anybody may still change, and the office
 * routinely does: a run is raised the moment a site is called finished, and which trucks go is
 * settled afterwards.
 *
 * Saving reprints the run's sheet, on the server: the sheet carries one page per lorry, so a fleet
 * that has moved leaves a driver without paper. The reprint replaces the sheet rather than filing a
 * second one — a run has exactly one, and it names the trucks now assigned.
 */
export function EditReturnOrderVehiclesModal({
	isOpen,
	onClose,
	onComplete,
	returnOrderId,
}: EditReturnOrderVehiclesModalProps) {
	/** Hooks */
	const { t } = useTranslation();
	const { defaultValues, loadFailed } = useGuardedDefaultValues(async () => {
		const response = await client.stock["return-orders"][":returnOrderId"].$get({
			param: { returnOrderId },
		});
		const data = await responseHandler(response);
		// `vehicleId` and not `id`: the rows are links, and the picker names the truck.
		const vehicleIds = guardArray(data.returnOrder.vehicles).map((vehicle) => vehicle.vehicleId);
		return returnOrderVehiclesFormSchema.parse({ vehicleIds });
	});
	const methods = useForm({
		resolver: resolver(returnOrderVehiclesFormSchema),
		defaultValues,
	});

	/** Functions */
	async function handleOnSubmit(json: ReturnOrderVehiclesFormSchema) {
		// The whole set in one request, so the server works out the difference and reprints the
		// sheet once. Sending it truck by truck would file a sheet per truck touched.
		const response = await client.stock["return-orders"][":returnOrderId"].vehicles.$patch({
			param: { returnOrderId },
			json: { vehicleIds: guardArray(json.vehicleIds) },
		});
		await responseHandler(response);

		await onComplete?.();
		onClose();
	}

	/** Render */
	return (
		<FormProvider {...methods}>
			<Modal
				title={t("stock.return_edit_vehicles")}
				isOpen={isOpen}
				onClose={onClose}
				onSubmit={methods.handleSubmit(handleOnSubmit)}
				isLoading={methods.formState.isLoading && !loadFailed}
			>
				{loadFailed ? (
					<ModalLoadFailed onClose={onClose} />
				) : (
					<Fragment>
						<Modal.Body>
							<ReturnOrderFormVehicles />
						</Modal.Body>
						<Modal.FooterForm />
					</Fragment>
				)}
			</Modal>
		</FormProvider>
	);
}
